import { useQuery } from '@tanstack/react-query';

import { CountryFlagWithLabel } from '@/components/CountryFlag';
import { Loader } from '@/components/Loading/Loader';
import SelectCountry from '@/components/Select/SelectCountry';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { SupportedCountry } from '@/constants/country';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { getPath } from '@/lib/api/path';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { FIVE_MINUTES } from '@/lib/query';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

export interface Country {
  id?: string;
  name?: string;
  flagUrl?: string;
  code?: string;
}

// API関連の型と関数をローカルで定義
type GetCountriesResponse = ApiResponseBody<'/v2/division/countries', 'get'>;

// 国データ取得API関数
const fetchCountries = async (): Promise<GetCountriesResponse> => {
  const res = await apiClient.GET('/v2/division/countries');
  return getDataOrThrowApiError(res);
};

// API レスポンスの国データの型
type ApiCountry = GetCountriesResponse['countries'][0];

type Props = CFormItemProps & {
  value?: Country | null;
  onChange?: (value?: Country | null) => void;
  /** 空の場合の表示テキスト */
  placeholder?: string;
  /** 空の選択を許可するか */
  allowEmpty?: boolean;
};

/**
 * 国一覧を取得するReact Queryフック
 * CFormCountryItem専用のローカルフック
 */
function useCountries() {
  return useQuery({
    queryKey: [getPath('/v2/division/countries')] as const,
    queryFn: fetchCountries,
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Card-based Form / CountryItem
 * 国情報（フラグ、国名、国コード）を表示するフォームアイテム
 */
export const CFormCountryItem = buildCFormItem((props: Props) => {
  const {
    value,
    placeholder = '-',
    onChange,
    allowEmpty = false,
    readonly,
    ...restProps
  } = props;
  // 国データを取得
  const { data: countriesData, isLoading, error } = useCountries();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.country',
  });

  // valueのcodeからnameを解決する関数
  const resolveCountryName = (country: Country | null): Country | null => {
    if (!country || !country.code) return country;

    // 既にnameがある場合はそのまま返す
    if (country.name) return country;

    // nameが空の場合、countriesDataから名前を解決
    if (countriesData?.countries) {
      const foundCountry = countriesData.countries.find(
        (c: ApiCountry) => c.code === country.code,
      );
      if (foundCountry) {
        return {
          ...country,
          name: foundCountry.name,
        };
      }
    }

    return country;
  };

  // 解決済みの値
  const resolvedValue = resolveCountryName(value ?? null);

  const renderCountryDisplay = (country: Country | null) => {
    if (!country || !country.name) {
      return (
        <Text variant="p" className="text-neutral-muted-on-fill">
          {placeholder}
        </Text>
      );
    }

    return (
      <CountryFlagWithLabel
        countryCode={country.code}
        label={
          <Text
            variant="labelSemiLarge"
            className="text-neutral-on-fill truncate"
          >
            {country.name}
            {country.code && ` (${country.code})`}
          </Text>
        }
        className="min-w-0"
      />
    );
  };

  const renderCountrySelector = () => {
    // ローディング中の表示
    if (isLoading) {
      return (
        <Button
          variant="outline"
          disabled
          className={cn('justify-start w-full', 'bg-neutral-fill')}
        >
          <Loader size="small" className="mr-2" />
          <Text variant="p" className="text-neutral-muted-on-fill">
            {t('loading')}
          </Text>
        </Button>
      );
    }

    // エラー時の表示
    if (error || !countriesData) {
      return (
        <Button
          variant="outline"
          disabled
          className={cn(
            'justify-start w-full text-error-on-neutral-fill border-error-border',
            'bg-neutral-fill',
          )}
        >
          <Text variant="p" className="text-error-on-neutral-fill">
            {t('loadFailed')}
          </Text>
        </Button>
      );
    }

    return (
      <SelectCountry
        value={value?.code as SupportedCountry | undefined}
        onChange={(value?: keyof typeof SupportedCountry) => {
          onChange?.(value ? { code: value } : null);
        }}
        onClear={allowEmpty ? () => onChange?.(null) : undefined}
      />
    );
  };

  return (
    <CFormItemBase
      {...restProps}
      readComponent={renderCountryDisplay(resolvedValue ?? null)}
      writeComponent={
        readonly
          ? renderCountryDisplay(resolvedValue ?? null)
          : renderCountrySelector()
      }
    />
  );
});
